import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  Store, RefreshCw, ShoppingCart, Package, Globe, ArrowRight, Plus
} from "lucide-react";

// Marketplace components
import { HepsiBuradaSettings, N11Settings } from "@/components/marketplaces/TurkeyMarketplaces";
import { TrendyolSettings, CiceksepetiSettings } from "@/components/marketplaces/TrendyolCiceksepeti";
import { AmazonSettings, NoonSettings, NamshiSettings } from "@/components/marketplaces/GlobalMarketplaces";

const API = "/api";

// Pazaryeri logoları ve renkleri
const MARKETPLACE_CONFIG = {
  noon: { color: "bg-yellow-500", textColor: "text-yellow-700", bgLight: "bg-yellow-50" },
  namshi: { color: "bg-purple-500", textColor: "text-purple-700", bgLight: "bg-purple-50" },
  trendyol: { color: "bg-orange-500", textColor: "text-orange-700", bgLight: "bg-orange-50" },
  hepsiburada: { color: "bg-orange-600", textColor: "text-orange-700", bgLight: "bg-orange-50" },
  n11: { color: "bg-green-500", textColor: "text-green-700", bgLight: "bg-green-50" },
  ciceksepeti: { color: "bg-pink-500", textColor: "text-pink-700", bgLight: "bg-pink-50" },
  amazon: { color: "bg-amber-500", textColor: "text-amber-700", bgLight: "bg-amber-50" }
};

export default function MarketplacesPage() {
  const { token } = useAuth();
  const [marketplaces, setMarketplaces] = useState({});
  const [credentials, setCredentials] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const [mpRes, credRes, statsRes] = await Promise.all([
        axios.get(`${API}/marketplaces?token=${token}`),
        axios.get(`${API}/marketplaces/credentials?token=${token}`),
        axios.get(`${API}/marketplaces/stats?token=${token}`)
      ]);
      setMarketplaces(mpRes.data.marketplaces);
      setCredentials(credRes.data.credentials);
      setStats(statsRes.data.stats);
    } catch (error) {
      toast.error("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const connectedCount = Object.values(stats).filter(s => s.connected).length;
  const totalProducts = Object.values(stats).reduce((sum, s) => sum + s.products, 0);
  const totalOrders = Object.values(stats).reduce((sum, s) => sum + s.orders, 0);

  return (
    <div className="space-y-6 fade-in" data-testid="marketplaces-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Pazaryerleri</h1>
          <p className="text-slate-500 mt-1">Tüm pazaryeri entegrasyonlarınızı yönetin</p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Yenile
        </Button>
      </div>

      {/* Özet Kartlar */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Store className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{connectedCount}</p>
                <p className="text-sm text-slate-500">Bağlı Pazaryeri</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <Package className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{totalProducts}</p>
                <p className="text-sm text-slate-500">Toplam Ürün</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <ShoppingCart className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{totalOrders}</p>
                <p className="text-sm text-slate-500">Toplam Sipariş</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-100 rounded-lg">
                <Globe className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{Object.keys(marketplaces).length}</p>
                <p className="text-sm text-slate-500">Desteklenen</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Genel Bakış</TabsTrigger>
          <TabsTrigger value="noon">NOON</TabsTrigger>
          <TabsTrigger value="namshi">NAMSHI</TabsTrigger>
          <TabsTrigger value="trendyol">Trendyol</TabsTrigger>
          <TabsTrigger value="hepsiburada">HepsiBurada</TabsTrigger>
          <TabsTrigger value="n11">N11</TabsTrigger>
          <TabsTrigger value="ciceksepeti">Çiçeksepeti</TabsTrigger>
          <TabsTrigger value="amazon">Amazon</TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(marketplaces).map(([key, mp]) => {
              const config = MARKETPLACE_CONFIG[key] || { color: "bg-gray-500", bgLight: "bg-gray-50" };
              const stat = stats[key] || {};
              const cred = credentials.find(c => c.marketplace === key);

              return (
                <Card key={key} className={`${config.bgLight} border-2 ${stat.connected ? "border-green-300" : "border-transparent"}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 ${config.color} rounded-lg flex items-center justify-center text-white font-bold text-sm`}>
                          {mp.name.substring(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <CardTitle className="text-lg">{mp.name}</CardTitle>
                          <CardDescription>{mp.region}</CardDescription>
                        </div>
                      </div>
                      {stat.connected ? (
                        <Badge className="bg-green-100 text-green-700">Bağlı</Badge>
                      ) : (
                        <Badge variant="outline">Bağlı Değil</Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between text-sm mb-4">
                      <span className="text-slate-600">Ürün: <strong>{stat.products || 0}</strong></span>
                      <span className="text-slate-600">Sipariş: <strong>{stat.orders || 0}</strong></span>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        className="w-full"
                        onClick={() => setActiveTab(key)}
                      >
                        {stat.connected ? "Yönet" : "Bağlan"}
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Her pazaryeri için ayrı tab - Özelleştirilmiş bileşenler */}
        <TabsContent value="hepsiburada" className="space-y-4">
          <HepsiBuradaSettings 
            token={token}
            credentials={credentials.find(c => c.marketplace === "hepsiburada")}
            onSave={async (data) => {
              const existing = credentials.find(c => c.marketplace === "hepsiburada");
              if (existing) {
                await axios.put(`${API}/marketplaces/credentials/${existing.id}?token=${token}`, data);
              } else {
                await axios.post(`${API}/marketplaces/credentials?token=${token}`, data);
              }
              fetchData();
            }}
          />
        </TabsContent>

        <TabsContent value="n11" className="space-y-4">
          <N11Settings 
            token={token}
            credentials={credentials.find(c => c.marketplace === "n11")}
            onSave={async (data) => {
              const existing = credentials.find(c => c.marketplace === "n11");
              if (existing) {
                await axios.put(`${API}/marketplaces/credentials/${existing.id}?token=${token}`, data);
              } else {
                await axios.post(`${API}/marketplaces/credentials?token=${token}`, data);
              }
              fetchData();
            }}
          />
        </TabsContent>

        <TabsContent value="trendyol" className="space-y-4">
          <TrendyolSettings 
            token={token}
            credentials={credentials.find(c => c.marketplace === "trendyol")}
            onSave={async (data) => {
              const existing = credentials.find(c => c.marketplace === "trendyol");
              if (existing) {
                await axios.put(`${API}/marketplaces/credentials/${existing.id}?token=${token}`, data);
              } else {
                await axios.post(`${API}/marketplaces/credentials?token=${token}`, data);
              }
              fetchData();
            }}
          />
        </TabsContent>

        <TabsContent value="ciceksepeti" className="space-y-4">
          <CiceksepetiSettings 
            token={token}
            credentials={credentials.find(c => c.marketplace === "ciceksepeti")}
            onSave={async (data) => {
              const existing = credentials.find(c => c.marketplace === "ciceksepeti");
              if (existing) {
                await axios.put(`${API}/marketplaces/credentials/${existing.id}?token=${token}`, data);
              } else {
                await axios.post(`${API}/marketplaces/credentials?token=${token}`, data);
              }
              fetchData();
            }}
          />
        </TabsContent>

        <TabsContent value="amazon" className="space-y-4">
          <AmazonSettings 
            token={token}
            credentials={credentials.find(c => c.marketplace === "amazon")}
            onSave={async (data) => {
              const existing = credentials.find(c => c.marketplace === "amazon");
              if (existing) {
                await axios.put(`${API}/marketplaces/credentials/${existing.id}?token=${token}`, data);
              } else {
                await axios.post(`${API}/marketplaces/credentials?token=${token}`, data);
              }
              fetchData();
            }}
          />
        </TabsContent>

        <TabsContent value="noon" className="space-y-4">
          <NoonSettings 
            token={token}
            credentials={credentials.find(c => c.marketplace === "noon")}
            onSave={async (data) => {
              const existing = credentials.find(c => c.marketplace === "noon");
              if (existing) {
                await axios.put(`${API}/marketplaces/credentials/${existing.id}?token=${token}`, data);
              } else {
                await axios.post(`${API}/marketplaces/credentials?token=${token}`, data);
              }
              fetchData();
            }}
          />
        </TabsContent>

        <TabsContent value="namshi" className="space-y-4">
          <NamshiSettings 
            token={token}
            credentials={credentials.find(c => c.marketplace === "namshi")}
            onSave={async (data) => {
              const existing = credentials.find(c => c.marketplace === "namshi");
              if (existing) {
                await axios.put(`${API}/marketplaces/credentials/${existing.id}?token=${token}`, data);
              } else {
                await axios.post(`${API}/marketplaces/credentials?token=${token}`, data);
              }
              fetchData();
            }}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
